import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Globe, Lock } from "lucide-react";
import LoadingScreen from "./LoadingScreen";
import { toast } from "sonner";

// Public page for /share/:token. Renders read-only workspace data depending
// on the share mode (public / invite / secured). Invite + secured require
// the viewer to be signed in (and secured additionally requires a password).
export default function ShareView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [accessing, setAccessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: m } = await api.get(`/share/${token}`);
        setMeta(m);
      } catch (e) {
        setError(e?.response?.data?.detail || "Share not found");
      }
      setLoading(false);
    })();
  }, [token]);

  // For public mode, fetch the events immediately. For invite/secured, wait
  // for the user to authenticate (and provide a password if required).
  useEffect(() => {
    if (!meta) return;
    if (meta.mode === "public") {
      access();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.mode]);

  const access = async () => {
    setAccessing(true);
    try {
      const { data: d } = await api.post(`/share/${token}/access`, { password: password || null });
      setData(d);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Cannot access";
      toast.error(msg);
    }
    setAccessing(false);
  };

  if (loading) return <LoadingScreen label="Opening share…" />;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#EDEDED]">
      <div className="max-w-sm w-full text-center space-y-2 px-6">
        <div className="text-2xl font-display">Share unavailable</div>
        <div className="text-sm text-[#A1A1AA]">{error}</div>
        <button onClick={() => navigate("/")} className="mt-4 px-4 py-2 rounded bg-[#EDEDED] text-[#0A0A0A] text-xs font-semibold">Go home</button>
      </div>
    </div>
  );
  if (!meta) return null;

  // Gate: invite/secured requires sign-in.
  if ((meta.mode === "invite" || meta.mode === "secured") && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#EDEDED] px-6">
        <div className="max-w-sm w-full text-center space-y-3" data-testid="share-gate-signin">
          <div className="flex items-center justify-center gap-2">
            <Lock className="w-5 h-5 text-[#5A90F5]" />
            <div className="text-2xl font-display">Sign in to view</div>
          </div>
          <div className="text-sm text-[#A1A1AA]">
            <span className="text-[#EDEDED]">{meta.name}</span> is shared with specific people. Sign in to confirm your access.
          </div>
          <button onClick={() => navigate(`/login?next=/share/${token}`)} className="mt-4 px-4 py-2 rounded bg-[#5A90F5] text-[#0A0A0A] text-xs font-semibold">Sign in</button>
        </div>
      </div>
    );
  }

  // Secured: ask for password before accessing.
  if (meta.mode === "secured" && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#EDEDED] px-6">
        <div className="max-w-sm w-full space-y-3" data-testid="share-gate-password">
          <div className="flex items-center justify-center gap-2">
            <Lock className="w-5 h-5 text-[#5A90F5]" />
            <div className="text-2xl font-display text-center">Enter password</div>
          </div>
          <div className="text-sm text-[#A1A1AA] text-center">
            <span className="text-[#EDEDED]">{meta.name}</span> is password-protected.
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") access(); }}
            placeholder="Password"
            className="w-full bg-[#141414] border border-[#2A2A2A] rounded-md px-3 py-2 outline-none focus:border-[#5A90F5] font-mono"
            data-testid="share-password-input"
            autoFocus
          />
          <button
            onClick={access}
            disabled={accessing || !password}
            className="w-full px-4 py-2 rounded bg-[#5A90F5] text-[#0A0A0A] text-xs font-semibold disabled:opacity-60"
            data-testid="share-password-submit"
          >
            {accessing ? "Checking…" : "View workspace"}
          </button>
        </div>
      </div>
    );
  }

  // Read-only render of the workspace events (lightweight; full visual node
  // graph would require rendering the heavy CalendarView for now we present a
  // listing — the read-only graph view is a follow-up).
  if (data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]" data-testid="share-readonly">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-5 h-5" style={{ color: data.workspace.color }} />
            <div className="text-3xl font-display">{data.workspace.name}</div>
            <span className="ml-auto text-[10px] uppercase tracking-widest text-[#A1A1AA]">{data.workspace.share_mode} · view-only</span>
          </div>
          {data.workspace.description && <p className="text-[#A1A1AA] mb-8">{data.workspace.description}</p>}
          <div className="space-y-2">
            {data.events.length === 0 && (
              <div className="text-sm text-[#A1A1AA]">No events to show.</div>
            )}
            {data.events.map((e) => (
              <div key={e.event_id} className="rounded-md border border-[#2A2A2A] bg-[#141414] px-4 py-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                  <span>{e.type}</span>
                  {e.class_name && <span>· {e.class_name}</span>}
                </div>
                <div className="text-base font-medium text-[#EDEDED] mt-0.5">{e.title || <span className="italic text-[#A1A1AA]">untitled</span>}</div>
                {e.start && (
                  <div className="text-[11px] text-[#A1A1AA] mt-1">
                    {new Date(e.start).toLocaleString()}
                    {e.end && e.end !== e.start ? ` – ${new Date(e.end).toLocaleString()}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return <LoadingScreen label="Opening share…" />;
}
